/**
 * SHA-256。
 *
 * 为什么自己实现而不调 `crypto.subtle`：MAVLink 2 的签名要在**同步**的发送路径上算
 * （每帧一次），而 WebCrypto 只有异步接口 —— 把签名做成 async 会污染整条发送链路。
 * 浏览器里也没有同步的摘要 API（去掉了 `crypto.subtle.sign` 的同步变体）。
 *
 * 实现是标准的 FIPS 180-4。正确性由 `tests/sha256.test.ts` 用公开测试向量钉住。
 */
import { MAVLINK_SIGNATURE_HASH_LENGTH } from './frame';

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

/** 增量式 SHA-256：签名要按「密钥 + 帧头 + 载荷 + 校验 + 链路 + 时间戳」分多段喂入。 */
export class Sha256 {
  private readonly state = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  private readonly block = new Uint8Array(64);
  private readonly words = new Uint32Array(64);
  private blockLength = 0;
  private totalLength = 0;

  update(bytes: Uint8Array): void {
    this.totalLength += bytes.length;
    let offset = 0;

    if (this.blockLength > 0) {
      const needed = Math.min(64 - this.blockLength, bytes.length);
      this.block.set(bytes.subarray(0, needed), this.blockLength);
      this.blockLength += needed;
      offset = needed;
      if (this.blockLength === 64) {
        this.compress(this.block, 0);
        this.blockLength = 0;
      }
    }

    while (offset + 64 <= bytes.length) {
      this.compress(bytes, offset);
      offset += 64;
    }

    if (offset < bytes.length) {
      this.block.set(bytes.subarray(offset), 0);
      this.blockLength = bytes.length - offset;
    }
  }

  digest(): Uint8Array {
    const bitLength = this.totalLength * 8;

    // 填充：0x80，补零到 56 字节，再写 64 位大端比特长度
    this.block[this.blockLength] = 0x80;
    this.blockLength += 1;

    if (this.blockLength > 56) {
      this.block.fill(0, this.blockLength, 64);
      this.compress(this.block, 0);
      this.blockLength = 0;
    }
    this.block.fill(0, this.blockLength, 56);

    const high = Math.floor(bitLength / 0x100000000);
    const low = bitLength >>> 0;
    const tail = new DataView(this.block.buffer, this.block.byteOffset + 56, 8);
    tail.setUint32(0, high, false);
    tail.setUint32(4, low, false);
    this.compress(this.block, 0);

    const out = new Uint8Array(32);
    const view = new DataView(out.buffer);
    for (let index = 0; index < 8; index += 1) {
      view.setUint32(index * 4, this.state[index] ?? 0, false);
    }
    return out;
  }

  private compress(chunk: Uint8Array, offset: number): void {
    const words = this.words;
    for (let index = 0; index < 16; index += 1) {
      const base = offset + index * 4;
      words[index] =
        (((chunk[base] ?? 0) << 24) |
          ((chunk[base + 1] ?? 0) << 16) |
          ((chunk[base + 2] ?? 0) << 8) |
          (chunk[base + 3] ?? 0)) >>>
        0;
    }
    for (let index = 16; index < 64; index += 1) {
      const w15 = words[index - 15] ?? 0;
      const w2 = words[index - 2] ?? 0;
      const s0 = ((w15 >>> 7) | (w15 << 25)) ^ ((w15 >>> 18) | (w15 << 14)) ^ (w15 >>> 3);
      const s1 = ((w2 >>> 17) | (w2 << 15)) ^ ((w2 >>> 19) | (w2 << 13)) ^ (w2 >>> 10);
      words[index] = ((words[index - 16] ?? 0) + s0 + (words[index - 7] ?? 0) + s1) >>> 0;
    }

    let a = this.state[0] ?? 0;
    let b = this.state[1] ?? 0;
    let c = this.state[2] ?? 0;
    let d = this.state[3] ?? 0;
    let e = this.state[4] ?? 0;
    let f = this.state[5] ?? 0;
    let g = this.state[6] ?? 0;
    let h = this.state[7] ?? 0;

    for (let index = 0; index < 64; index += 1) {
      const s1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const temp1 = (h + s1 + ch + (K[index] ?? 0) + (words[index] ?? 0)) >>> 0;
      const s0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const temp2 = (s0 + maj) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    this.state[0] = ((this.state[0] ?? 0) + a) >>> 0;
    this.state[1] = ((this.state[1] ?? 0) + b) >>> 0;
    this.state[2] = ((this.state[2] ?? 0) + c) >>> 0;
    this.state[3] = ((this.state[3] ?? 0) + d) >>> 0;
    this.state[4] = ((this.state[4] ?? 0) + e) >>> 0;
    this.state[5] = ((this.state[5] ?? 0) + f) >>> 0;
    this.state[6] = ((this.state[6] ?? 0) + g) >>> 0;
    this.state[7] = ((this.state[7] ?? 0) + h) >>> 0;
  }
}

export function sha256(bytes: Uint8Array): Uint8Array {
  const hasher = new Sha256();
  hasher.update(bytes);
  return hasher.digest();
}

/** 取 SHA-256 结果的前 6 字节作为 MAVLink 2 的签名。 */
export function signatureFromDigest(digest: Uint8Array): Uint8Array {
  return digest.subarray(0, MAVLINK_SIGNATURE_HASH_LENGTH);
}
